import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import useSiteSelectorQuery from 'queries/useSiteSelectorQuery';
import ClearSelectedSiteHandler from '@activebrands/core-web/components/ClearSelectedSiteHandler';
import WrongSiteHandler from '@activebrands/core-web/components/WrongSiteHandler';
import AnimatedOverlay from '@activebrands/core-web/components/overlays/AnimatedOverlay';
import BackdropOverlay from '@activebrands/core-web/components/overlays/BackdropOverlay';
import { OVERLAY_ID } from '@activebrands/core-web/config/overlay';
import PageContainer from '@activebrands/core-web/containers/PageContainer';
import { GridCheck } from '@activebrands/core-web/libs/GridCheck';
import useReInitWidgets from '@activebrands/core-web/libs/lipscore/hooks/useReInitWidgets';
import overlay, { useOverlaySubscriber } from '@activebrands/core-web/libs/overlay';
import { setPage } from '@activebrands/core-web/state/page/actions';
import { useTypedDispatch } from '@activebrands/core-web/state/store';
import { resolveUser } from '@activebrands/core-web/state/user/actions';
import { setSiteSelectorCookie } from '@activebrands/core-web/utils/handle-site-selector-cookie';
import objectKeysToCamelCase from '@grebban/utils/object/keysToCamelCase';
import loadable from '@loadable/component';
import { useLocation } from '@reach/router';
import PageLayout from 'components/Layout/PageLayout';
import NotificationsOverlay from 'components/overlays/NotificationsOverlay';

const isGridOverlayEnabled = process.env.REACT_APP_OVERLAY_GRID_ENABLED === 'true';

const ErrorOverlay = loadable(() => import('components/overlays/ErrorOverlay'));
const NavigationOverlay = loadable(() => import('templates/account/NavigationOverlay'));
const NotifyMeOverlay = loadable(() => import('templates/ProductPage/NotifyMeOverlay'));
const QuickshopOverlay = loadable(() => import('components/product/Quickshop/QuickshopOverlay'));
const ShopTheLookOverlay = loadable(() => import('components/content-components/ShopTheLook/ShopTheLookOverlay'));
const SizeGuideOverlay = loadable(() => import('templates/ProductPage/SizeGuideOverlay'));
const UnauthenticatedOverlay = loadable(() => import('components/auth/UnauthenticatedOverlay'));
const WrongSiteOverlay = loadable(() => import('components/overlays/WrongSiteOverlay'));
const ZoomGalleryOverlay = loadable(() => import('components/overlays/ZoomGalleryOverlay'));

const HookWrapper = ({ page }) => {
    const dispatch = useTypedDispatch();
    const { siteSelectorLinks } = useSiteSelectorQuery();
    const quickshopIsOpen = useOverlaySubscriber(OVERLAY_ID.QUICKSHOP);
    const wrongSitePopupIsOpen = useOverlaySubscriber(OVERLAY_ID.WRONG_SITE_OVERLAY);
    const currentSiteId = useSelector(state => state.application.aliasId);
    const currentSite = siteSelectorLinks.find(site => site.id == currentSiteId);
    const location = useLocation();

    useReInitWidgets(location.href);

    // This makes sure the quickshop overlays backdrop is on top of all other overlays
    const backdropStyles = quickshopIsOpen ? { zIndex: 'var(--zindex-overlay)' } : {};

    const handleWrongSitePopupBackdropOnClick = () => {
        overlay.close(OVERLAY_ID.WRONG_SITE_OVERLAY);
        setSiteSelectorCookie(currentSite.path);
        dispatch(resolveUser());
    };

    dispatch(setPage(page));

    return (
        <>
            <BackdropOverlay
                $style={backdropStyles}
                handleOnClick={wrongSitePopupIsOpen ? handleWrongSitePopupBackdropOnClick : undefined}
                isVisible={wrongSitePopupIsOpen}
            />
            {page.type !== 'siteSelectorPage' && (
                <WrongSiteHandler alternateHrefs={page.data?.hrefs ? objectKeysToCamelCase(page.data.hrefs) : []} />
            )}
        </>
    );
};

HookWrapper.propTypes = {
    page: PropTypes.object.isRequired,
};

const Layout = ({ element, props: pageProps }) => {
    const page = pageProps.pageContext?.page || {};

    return (
        <>
            <PageContainer page={page}>
                <PageLayout {...pageProps} element={element}>
                    {element}
                </PageLayout>
            </PageContainer>
            {isGridOverlayEnabled && <GridCheck />}
            <NotificationsOverlay />
            <AnimatedOverlay
                lockScroll
                component={NotifyMeOverlay}
                $style={{ height: 'auto', maxHeight: '100dvh', overflow: 'auto' }}
                id={OVERLAY_ID.NOTIFY_ME}
            />
            <AnimatedOverlay lockScroll component={SizeGuideOverlay} id={OVERLAY_ID.SIZEGUIDE} />
            <AnimatedOverlay
                lockScroll
                $style={{
                    height: 'calc(100dvh - var(--height-header))',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: ['flex-end', null, null, null, null, 'flex-start'],
                }}
                component={UnauthenticatedOverlay}
                id={OVERLAY_ID.UNAUTHENTICATED}
            />
            <AnimatedOverlay
                lockScroll
                $style={{ zIndex: 'var(--zindex-pdp-gallery)', top: '0', height: '100dvh' }}
                component={ZoomGalleryOverlay}
                id={OVERLAY_ID.ZOOM_GALLERY}
            />
            <AnimatedOverlay
                lockScroll
                $style={{ bottom: '0' }}
                animation="slideLTR"
                component={NavigationOverlay}
                id={OVERLAY_ID.ACCOUNT_NAVIGATION}
            />
            <AnimatedOverlay
                lockScroll
                $style={{ height: 'auto', top: '50%', zIndex: 'var(--zindex-wrong-site-popup)' }}
                animation="slideBTM"
                animationDuration="none"
                component={WrongSiteOverlay}
                id={OVERLAY_ID.WRONG_SITE_OVERLAY}
            />
            <AnimatedOverlay lockScroll component={ShopTheLookOverlay} id={OVERLAY_ID.SHOP_THE_LOOK} />
            <AnimatedOverlay
                lockScroll
                $style={{
                    bottom: 0,
                    display: ['initial', null, null, 'none'],
                    zIndex: 'calc(var(--zindex-overlay) + 1)',
                    width: '100%',
                    height: 'calc(100dvh - var(--height-header))',
                }}
                animation="slideBTT"
                component={QuickshopOverlay}
                id={OVERLAY_ID.QUICKSHOP}
            />
            <AnimatedOverlay
                lockScroll
                $style={{ height: 'auto', top: '50%', zIndex: 'calc(var(--zindex-overlay) + 1)' }}
                animation="slideBTM"
                animationDuration="none"
                component={ErrorOverlay}
                id={OVERLAY_ID.ERROR_OVERLAY}
            />
            <HookWrapper page={page} />
            <ClearSelectedSiteHandler />
        </>
    );
};

Layout.propTypes = {
    element: PropTypes.node.isRequired,
    props: PropTypes.object.isRequired,
};

export default Layout;
